import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  Label,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import TooltipContent from '../TooltipContent';
import ReferenceLineLabel from './ReferenceLineLabel';
import CurrenciesLabel from './CurrenciesLabel';
import AxisCountry from './AxisCountry';
import styled from 'styled-components';
import get from 'lodash/get';

const BarGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  min-height: 150px;
  position: relative;
  .recharts-cartesian-axis-tick text {
    fill: #212529;
    font-size: 12px;
  }

  .recharts-bar-rectangle:hover {
    cursor: ${(props) => (props.popin ? 'pointer' : 'normal')};
  }
`;

const PopinContainer = styled.div`
  width: 99%;
  z-index: 5;
  background-color: white;
  border: 1px solid rgba(173, 181, 89, 0.1);
  top: ${(props) => props.top}px;
  position: absolute;
`;

class VerticalBarGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      hovering: false
    };
  }

  componentDidUpdate(prevProps) {
    if (
      (prevProps.order && prevProps.order !== this.props.order) ||
      JSON.stringify(this.props.data) !== JSON.stringify(prevProps.data)
    ) {
      this.setState({ open: false });
    }
  }

  indexData() {
    const { data, popin } = this.props;
    const { open } = this.state;
    let dataIndexed = [];
    data.forEach((set, indexSet) => {
      dataIndexed.push([]);
      return set.forEach((item, index) => {
        dataIndexed[indexSet].push({
          ...item,
          name: { name: item.name, index: index }
        });
      });
    });
    if (popin && open !== false) {
      dataIndexed = this.handleOpen(dataIndexed);
    }
    return dataIndexed;
  }

  formatData(data) {
    const { labels } = this.props;
    const aggregatedData = [];
    data.forEach((set, indexSet) => {
      set.forEach((item, index) => {
        let aggregatedItem = { ...item };

        //set labels for multisets
        aggregatedItem[labels[indexSet]] = item.value.value
          ? item.value.value
          : item.value;

        //merge sets
        if (aggregatedData[index]) {
          aggregatedData[index] = {
            ...aggregatedData[index],
            ...aggregatedItem
          };
        } else {
          aggregatedData.push(aggregatedItem);
        }
      });
    });
    return aggregatedData;
  }

  getHoverData() {
    const { hovering } = this.state;
    const { data } = this.props;
    let hoverData = [];
    if (hovering) {
      data.forEach((set) => {
        hoverData.push(set.find((item) => item.name === hovering));
      });
    }
    return hoverData;
  }

  handleOpen(data) {
    const newData = [];
    if (!data) {
      return newData;
    }
    const { open } = this.state;
    const { heightPopin = 5 } = this.props;
    data.forEach((set, indexSet) => {
      newData.push([]);
      set.forEach((entry) => {
        if (!entry.name.temporary) {
          newData[indexSet].push(entry);
        }
      });
      for (let step = 1; step <= heightPopin; step++) {
        newData[indexSet].splice(open + step, 0, {
          name: { temporary: true },
          value: ''
        });
      }
    });
    return newData;
  }

  renderYAxis(data) {
    const { customAxis, yAxisProps } = this.props;
    return customAxis ? (
      <YAxis
        type="category"
        dataKey="name"
        width={100}
        tickLine={false}
        tick={<AxisCountry data={data} />}
        {...yAxisProps}
      />
    ) : (
      <YAxis
        type="category"
        dataKey="store_name"
        width={100}
        tickLine={false}
        {...yAxisProps}
      />
    );
  }

  renderXAxis() {
    const { xAxisProps, currency } = this.props;
    return (
      <XAxis
        axisLine={false}
        tickCount={5}
        interval="preserveStart"
        scale="linear"
        tickFormatter={(value) =>
          currency
            ? value.toLocaleString('fr-FR', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })
            : value
        }
        type="number"
        domain={[0, (dataMax) => dataMax + dataMax / 4]}
        tickLine={false}
        {...xAxisProps}
      />
    );
  }

  renderReferenceLine() {
    const { referenceLineValue } = this.props;
    return (
      referenceLineValue && (
        <ReferenceLine
          x={referenceLineValue.value}
          strokeWidth={3}
          stroke="#e25d34"
        >
          <Label
            offset={5}
            value={referenceLineValue.value}
            content={
              <ReferenceLineLabel currency={referenceLineValue.currency_code} />
            }
            position="top"
          />
        </ReferenceLine>
      )
    );
  }

  renderTooltip() {
    const { hovering, coordinates } = this.state;
    if (!hovering) {
      return null;
    }
    const { singleBar, labels, currency, tooltipContent } = this.props;
    const hoverData = this.getHoverData();
    return (
      <Tooltip
        active
        content={
          <div className="rc-tooltip overlayTooltip rc-tooltip-placement-top rc-tooltip-inner">
            <div className="rc-tooltip-content">
              <div className="rc-tooltip-arrow" />
              <div className="rc-tooltip-inner" role="tooltip">
                <TooltipContent
                  singleBar={singleBar}
                  labels={labels}
                  currency={currency}
                  {...tooltipContent}
                  data={hoverData}
                />
              </div>
            </div>
          </div>
        }
        cursor={false}
        position={coordinates}
      />
    );
  }

  handlePopinOpen(props) {
    const { open } = this.state;
    const { popin } = this.props;
    if (popin) {
      if (typeof popin === 'function') {
        popin({ ...props, onClose: () => this.setState({ open: false }) });
      }
      this.setState({
        open:
          open !== false && open === props.name.index ? false : props.name.index
      });
    }
  }

  renderBars(data) {
    const { hovering } = this.state;
    const { labels, colors, activeColor, BarLabels } = this.props;
    return data.map((set, index) => (
      <Bar
        key={`bar-${index}`}
        isAnimationActive={false}
        barSize={30}
        onClick={(props) => this.handlePopinOpen(props)}
        dataKey={labels[index]}
      >
        <LabelList
          popin
          dataKey="label"
          referenceLineValue
          content={
            BarLabels ? (
              <BarLabels
                data={get(data, '[0]')}
                handleOpen={(data) => this.handlePopinOpen(data)}
              />
            ) : (
              CurrenciesLabel
            )
          }
          position="right"
        />
        {set.map((entry, indexSet) => (
          <Cell
            height={30}
            key={`cell-${index}-${indexSet}`}
            fill={entry.color_flag ? activeColor : colors[0]}
            opacity={hovering === false || hovering === entry.name ? 1 : 0.3}
          />
        ))}
      </Bar>
    ));
  }

  render() {
    const { popin, popinContent } = this.props;
    let { open } = this.state;
    let dataIndexed = this.indexData();
    const formattedData = this.formatData(dataIndexed);

    return (
      <BarGraphContainer popin={popin}>
        {popin && open !== false && (
          <PopinContainer top={(open + 1) * 45 + 30}>
            {popinContent}
          </PopinContainer>
        )}
        <ResponsiveContainer
          width="100%"
          height={formattedData.length !== 1 ? 46 * formattedData.length : 100}
          minHeight={400}
        >
          <BarChart
            data={formattedData}
            layout="vertical"
            barCategoryGap={16}
            barGap={16}
            margin={{
              top: 20,
              right: 0,
              left: 0,
              bottom:
                formattedData.length === 3 || formattedData.length === 2
                  ? -30
                  : 0
            }}
          >
            <CartesianGrid horizontal={false} stroke="#e4e4e4" />
            {this.renderYAxis(formattedData)}
            {this.renderXAxis()}
            {this.renderReferenceLine()}
            {this.renderTooltip()}
            {this.renderBars(dataIndexed)}
          </BarChart>
        </ResponsiveContainer>
      </BarGraphContainer>
    );
  }
}

export default VerticalBarGraph;
